import { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, styled } from '@mui/material';
import { theme } from '~/shared/styles/theme';
import { ROUTES } from '~/shared/constants';
import { useQuery, useMutation } from '@apollo/client';
import { AUTH_STATUS } from '~/graphql/queries';
import { RECORD_LOGIN } from '~/graphql/mutations';
import { reportError } from '~/shared/utils/sentry';

/**
 * ProtectedRoute component ensures that only authenticated users can access
 * wrapped routes. It checks authentication status and:
 * - Shows a loading state while verifying authentication
 * - Redirects to / (login page) if not authenticated, using replace to avoid
 *   back-button loops
 * - Renders child routes via <Outlet /> if authenticated
 *
 * Authentication methods:
 * 1. Auth0 interactive login (standard flow)
 * 2. Programmatic token in httpOnly cookie (for "No" invitation flow)
 *
 */
export const ProtectedRoute = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: auth0Loading } = useAuth0();

  // Check if request is authenticated via programmatic token cookie
  // This query only checks authentication, not database user existence
  const { data, loading: authLoading } = useQuery(AUTH_STATUS, {
    skip: isAuthenticated, // Skip if already authenticated via Auth0
  });

  // User is authenticated if:
  // 1. Auth0 says they're authenticated, OR
  // 2. The authStatus query says they're authenticated (cookie is valid)
  const hasProgrammaticAuth = Boolean(data?.authStatus?.isAuthenticated);
  const isUserAuthenticated = isAuthenticated || hasProgrammaticAuth;

  const [recordLogin] = useMutation(RECORD_LOGIN);

  const isLoading = auth0Loading || (authLoading && !isAuthenticated);

  useEffect(() => {
    if (isUserAuthenticated && !sessionStorage.getItem('loginRecorded')) {
      sessionStorage.setItem('loginRecorded', '1');
      (async () => {
        try {
          await recordLogin();
        } catch (error) {
          reportError(error, { operation: 'recordLogin' });
        }
      })();
    }
  }, [isUserAuthenticated, recordLogin]);

  useEffect(() => {
    // Wait for loading to complete
    if (isLoading) {
      return;
    }

    // Redirect unauthenticated users with replace: true to avoid back-button login loops
    if (!isUserAuthenticated) {
      navigate(ROUTES.LOGIN, { replace: true });
    }
  }, [isUserAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return <LoadingContainer />;
  }

  if (!isUserAuthenticated) {
    return null;
  }

  return <Outlet />;
};

const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  backgroundColor: theme.palette.custom.cream,
});
